import React, { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/apiFetch';
import { KeyTag } from '../components/KeyTag';
import type { KeyTagVariant } from '../components/KeyTag';
import {
  pageStyle,
  headingStyle,
  mutedTextStyle,
  errorBoxStyle,
  retryBtnStyle,
  matchCardStyle,
  matchCourtStripStyle,
  matchBodyStyle,
  matchTeamsRowStyle,
  matchTeamNameStyle,
  matchScoreSepStyle,
  matchMetaRowStyle,
  badgeStyle,
  statusColor,
  scoreActionWrapStyle,
  submitScoreBtnStyle,
  scoreFormRowStyle,
  scoreInputStyle,
  scoreInputLabelStyle,
  scoreVsSepStyle,
  approveScoreBtnStyle,
  rejectScoreBtnStyle,
  scoreActionErrorStyle,
  pendingBannerStyle,
} from '../App.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Match {
  id: string;
  scheduledAt: string;
  status: string;
  teamA?: { name?: string };
  teamB?: { name?: string };
  court?: { name?: string; id?: string };
  result?: { scoreA: number; scoreB: number; status: string } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function courtVariant(court?: { name?: string; id?: string }): KeyTagVariant {
  const seed = court?.id ?? court?.name ?? '';
  const sum = seed.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return sum % 2 === 0 ? 'pink' : 'green';
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
}

// ─── MyMatchesPage ────────────────────────────────────────────────────────────

export function MyMatchesPage(): React.JSX.Element {
  const { slug } = useTenant();
  const { user, token } = useAuth();

  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [myMatches, setMyMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Score submission state
  const [openSubmitId, setOpenSubmitId] = useState<string | null>(null);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Admin approve/reject state
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isAdmin = !!user?.roles?.some((r: string) =>
    ['ORG_ADMIN', 'LEAGUE_ADMIN', 'SUPER_ADMIN'].includes(r)
  );

  // Fetch player's own team memberships, then filter matches
  const load = useCallback(async () => {
    if (!slug || !user) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      // Load all matches across all leagues
      const [leaguesRes] = await Promise.all([
        apiFetch('/api/v1/leagues', { slug, token }),
      ]);
      if (!leaguesRes.ok) throw new Error('HTTP ' + leaguesRes.status);
      const leagues: { id: string }[] = await leaguesRes.json();

      // Fetch all matches for every league in parallel
      const matchArrays = await Promise.all(
        leagues.map(l =>
          apiFetch('/api/v1/matches?leagueId=' + l.id, { slug, token })
            .then(r => r.ok ? r.json() as Promise<Match[]> : [])
        )
      );
      const all: Match[] = matchArrays.flat();
      all.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
      setAllMatches(all);

      // Also fetch this user's player entries to know which teams they're on
      const playersRes = await apiFetch('/api/v1/players?userId=' + user.id, { slug, token });
      const myPlayers: { teamId: string }[] = playersRes.ok ? await playersRes.json() : [];
      const myTeamIds = new Set(myPlayers.map(p => p.teamId));

      if (isAdmin || myTeamIds.size === 0) {
        // Admins see all; unrosted users see all too (they may need to submit)
        setMyMatches(all);
      } else {
        setMyMatches(all.filter(m =>
          (m.teamA && myTeamIds.has((m.teamA as { id?: string }).id ?? '')) ||
          (m.teamB && myTeamIds.has((m.teamB as { id?: string }).id ?? ''))
        ));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load matches');
    } finally { setLoading(false); }
  }, [slug, token, user, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const openSubmit = (matchId: string) => {
    setOpenSubmitId(matchId); setScoreA(0); setScoreB(0); setSubmitError(null);
  };
  const cancelSubmit = () => { setOpenSubmitId(null); setSubmitError(null); };

  const handleSubmitScore = async (matchId: string) => {
    if (!token) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await apiFetch(`/api/v1/matches/${matchId}/submit-score`, {
        method: 'POST', slug, token,
        body: JSON.stringify({ scoreA, scoreB }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error((b as { message?: string }).message || 'HTTP ' + res.status);
      }
      setOpenSubmitId(null); await load();
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit score');
    } finally { setSubmitting(false); }
  };

  const handleApprove = async (matchId: string) => {
    if (!token) return;
    setActioningId(matchId); setActionError(null);
    try {
      const res = await apiFetch(`/api/v1/matches/${matchId}/approve-score`, {
        method: 'POST', slug, token, body: JSON.stringify({}),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error((b as { message?: string }).message || 'HTTP ' + res.status);
      }
      await load();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to approve');
    } finally { setActioningId(null); }
  };

  const handleReject = async (matchId: string) => {
    if (!token) return;
    setActioningId(matchId); setActionError(null);
    try {
      const res = await apiFetch(`/api/v1/matches/${matchId}/reject-score`, {
        method: 'POST', slug, token, body: JSON.stringify({}),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error((b as { message?: string }).message || 'HTTP ' + res.status);
      }
      await load();
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Failed to reject');
    } finally { setActioningId(null); }
  };

  if (!user) {
    return (
      <main style={pageStyle}>
        <p style={mutedTextStyle}>Please sign in to view your matches.</p>
      </main>
    );
  }

  // Split matches into upcoming and past
  const now = Date.now();
  const upcoming = myMatches.filter(m => new Date(m.scheduledAt).getTime() >= now - 3_600_000);
  const past = myMatches.filter(m => new Date(m.scheduledAt).getTime() < now - 3_600_000);

  const renderMatch = (match: Match) => {
    const scoreLabel = match.result
      ? `${match.result.scoreA} – ${match.result.scoreB}`
      : 'vs';
    const resultStatus = match.result?.status ?? null;
    const variant = courtVariant(match.court);
    const isSubmitOpen = openSubmitId === match.id;
    const isActioning = actioningId === match.id;
    return (
      <div key={match.id} style={matchCardStyle}>
        <div style={matchCourtStripStyle}>
          <KeyTag
            number={match.court?.id ? parseInt(match.court.id.replace(/\D/g, ''), 10) || 1 : 1}
            variant={variant}
            size="sm"
            label={match.court?.name ?? 'Court'}
          />
        </div>
        <div style={matchBodyStyle}>
          <div style={matchTeamsRowStyle}>
            <span style={matchTeamNameStyle}>{match.teamA?.name ?? 'TBA'}</span>
            <span style={matchScoreSepStyle}>{scoreLabel}</span>
            <span style={matchTeamNameStyle}>{match.teamB?.name ?? 'TBA'}</span>
            <span style={badgeStyle(statusColor[match.status] ?? '#6b7280')}>
              {match.status}
            </span>
            {resultStatus && (
              <span style={badgeStyle(statusColor[resultStatus] ?? '#6b7280')}>
                {resultStatus}
              </span>
            )}
          </div>
          <div style={matchMetaRowStyle}>
            <span>{formatDate(match.scheduledAt)}</span>
            {match.court?.name && <span>{match.court.name}</span>}
          </div>
          {/* Score submission for SCHEDULED matches */}
          {match.status === 'SCHEDULED' && (
            <div style={scoreActionWrapStyle}>
              {!isSubmitOpen ? (
                <button style={submitScoreBtnStyle} onClick={() => openSubmit(match.id)}>
                  Submit Score
                </button>
              ) : (
                <div>
                  <div style={scoreFormRowStyle}>
                    <span style={scoreInputLabelStyle}>{match.teamA?.name ?? 'Team A'}</span>
                    <input type="number" min={0} max={99} value={scoreA}
                      onChange={e => setScoreA(Number(e.target.value))}
                      style={scoreInputStyle} />
                    <span style={scoreVsSepStyle}>–</span>
                    <input type="number" min={0} max={99} value={scoreB}
                      onChange={e => setScoreB(Number(e.target.value))}
                      style={scoreInputStyle} />
                    <span style={scoreInputLabelStyle}>{match.teamB?.name ?? 'Team B'}</span>
                  </div>
                  {submitError && <p style={scoreActionErrorStyle}>{submitError}</p>}
                  <div style={{ ...scoreFormRowStyle, marginTop: 10 }}>
                    <button style={approveScoreBtnStyle}
                      onClick={() => handleSubmitScore(match.id)} disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit'}
                    </button>
                    <button style={rejectScoreBtnStyle} onClick={cancelSubmit} disabled={submitting}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Admin approve/reject for PENDING results */}
          {resultStatus === 'PENDING' && isAdmin && (
            <div style={scoreActionWrapStyle}>
              <span style={pendingBannerStyle}>⏳ Score pending review</span>
              {actionError && <p style={scoreActionErrorStyle}>{actionError}</p>}
              <div style={{ ...scoreFormRowStyle, marginTop: 8 }}>
                <button style={approveScoreBtnStyle}
                  onClick={() => handleApprove(match.id)} disabled={isActioning}>
                  {isActioning ? 'Approving…' : '✓ Approve'}
                </button>
                <button style={rejectScoreBtnStyle}
                  onClick={() => handleReject(match.id)} disabled={isActioning}>
                  {isActioning ? 'Rejecting…' : '✗ Reject'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <main style={pageStyle}>
      <h1 style={headingStyle}>My Matches</h1>

      {loading && <p style={mutedTextStyle}>Loading matches…</p>}
      {error && (
        <div style={errorBoxStyle}>
          {error} <button style={retryBtnStyle} onClick={load}>Retry</button>
        </div>
      )}

      {!loading && !error && myMatches.length === 0 && (
        <p style={mutedTextStyle}>No matches found for your account.</p>
      )}

      {!loading && upcoming.length > 0 && (
        <>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700,
            color: 'rgba(45,32,21,0.65)', marginBottom: '0.75rem', marginTop: 0 }}>
            Upcoming
          </h2>
          {upcoming.map(renderMatch)}
        </>
      )}

      {!loading && past.length > 0 && (
        <>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700,
            color: 'rgba(45,32,21,0.65)', marginBottom: '0.75rem',
            marginTop: upcoming.length > 0 ? '2rem' : 0 }}>
            Past
          </h2>
          {past.map(renderMatch)}
        </>
      )}
    </main>
  );
}
